<script setup lang="ts">
import type { PopoverProps } from '@nuxt/ui';
import type { VNode } from 'vue';
import { unrefElement } from '@vueuse/core';
import { Primitive } from 'reka-ui';
import { computed, onBeforeUnmount, useTemplateRef } from 'vue';
import { injectHoverCardController } from '#layers/ui/composables/hover-card-context';

/**
 * Shows `#content` in the app-wide card owned by `DHoverCardProvider`. The content renders under the
 * provider, so it sees app-level injections only, not ones provided around this trigger.
 */
const { content, contentClass } = defineProps<{
	content?: PopoverProps['content'];
	/** Classes for the card body, which has no padding of its own. */
	contentClass?: string;
}>();

const slots = defineSlots<{
	default: () => VNode[];
	content: () => VNode[];
}>();

const controller = injectHoverCardController();
const trigger = useTemplateRef('trigger');

function triggerElement(): HTMLElement | undefined {
	const element = unrefElement(trigger);
	return element instanceof HTMLElement ? element : undefined;
}

function show(): void {
	const reference = triggerElement();
	if (!reference) return;

	controller.show({
		reference,
		renderContent: () => slots.content(),
		content,
		contentClass,
	});
}

function hide(): void {
	const reference = triggerElement();
	if (reference) controller.hide(reference);
}

// Touch has no hover; like Reka's HoverCardTrigger, taps don't open the card.
function onPointerEnter(event: PointerEvent): void {
	if (event.pointerType !== 'touch') show();
}

function onPointerLeave(event: PointerEvent): void {
	if (event.pointerType !== 'touch') hide();
}

const state = computed(() => {
	const reference = triggerElement();
	return reference && reference === controller.openReference.value ? 'open' : 'closed';
});

onBeforeUnmount(() => {
	const reference = triggerElement();
	if (reference) controller.release(reference);
});
</script>

<template>
	<Primitive
		ref="trigger"
		as-child
		:data-state="state"
		@pointerenter="onPointerEnter"
		@pointerleave="onPointerLeave"
		@focus="show"
		@blur="hide"
	>
		<slot />
	</Primitive>
</template>
